import React, { useEffect, useState, useRef, useCallback } from 'react';

interface NotificationToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  duration?: number | null;
  onClose: () => void;
  onMount?: (element: HTMLDivElement | null) => void;
  downloadUrl?: string;
  filename?: string;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  message,
  type,
  duration = 3000,
  onClose,
  onMount,
  downloadUrl,
  filename,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const hasMeasured = useRef(false);

  useEffect(() => {
    // Measure height and notify parent only once
    if (onMount && notificationRef.current && !hasMeasured.current) {
      onMount(notificationRef.current);
      hasMeasured.current = true;
    }
  });

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match exit animation duration
  }, [onClose]);

  useEffect(() => {
    // Fade in animation
    setIsVisible(true);

    // Auto-close timer (only if duration > 0)
    if (duration && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return 'bg-green-500 border-green-600';
      case 'error':
        return 'bg-red-500 border-red-600';
      case 'info':
      default:
        return 'bg-blue-500 border-blue-600';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'info':
      default:
        return 'ℹ';
    }
  };

  return (
    <div
      ref={notificationRef}
      className={`
        max-w-xl w-full sm:w-auto sm:min-w-96
        ${getTypeStyles()}
        text-white rounded-lg shadow-lg border
        transition-all duration-300 ease-in-out
        ${isVisible && !isExiting ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}
      `}
    >
      <div className="p-4 flex items-start space-x-3">
        <div className="flex-shrink-0">
          <span className="text-lg font-bold">{getIcon()}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium break-words leading-relaxed">
            {message}
          </p>
          {downloadUrl && filename && (
            <button
              type="button"
              className="inline-block mt-2 px-3 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-xs font-medium transition-colors cursor-pointer break-all"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();

                // Create anchor element only when clicked
                const anchor = document.createElement('a');
                anchor.style.display = 'none';
                anchor.href = downloadUrl;
                anchor.download = filename;
                anchor.target = '_blank';

                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
              }}
            >
              Download {filename}
            </button>
          )}
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-white hover:text-gray-200 transition-colors ml-2"
        >
          <span className="sr-only">Close</span>
          <span className="text-xl leading-none">×</span>
        </button>
      </div>
    </div>
  );
};

export default NotificationToast;
